<template>
  <div v-if="signupBannerVisible" class="loyalty">
    <AW23TextCard :title="text">
      <template #cta>
        <Button
          form="underline"
          @click="setSlideSignUp({ value: true, forceShow: true })"
        >
          Create Account
        </Button>
        <Button form="underline" @click="setSlideLogin(true)">Sign In</Button>
      </template>
    </AW23TextCard>
  </div>
  <FooterLinks />
  <SlideLogin />
  <StoreRedirect v-if="!isPlaywright" />
  <footer class="footer">
    <div
      class="footer-grid"
      :class="{ 'footer-grid--payments': footerPaymentMethods.length }"
    >
      <div class="footer-grid__brand">
        <div class="footer-grid__logo">
          <a href="/">
            <img
              class="footer__logo"
              src="https://cdn.shopify.com/s/files/1/0942/9062/files/monogram.svg?v=1652005246"
              width="82"
              height="66"
            />
          </a>
        </div>
        <div class="footer-grid__copyright">
          <p class="copyright">Ratandboa &copy; 2026</p>
        </div>
      </div>
      <ul
        v-if="footerPaymentMethods.length"
        class="footer-payments"
        aria-label="Payment methods"
      >
        <li v-for="method in footerPaymentMethods" :key="method.alt">
          <img
            class="footer-payments__icon"
            :src="method.src"
            :alt="method.alt"
          />
        </li>
      </ul>
      <div class="footer-grid__links">
        <ul class="links">
          <li>
            <Currency
              :options="dropdownoptions"
              theme="secondary"
              :selected="currencyCode"
              title="Currency"
              form="transparent"
              class="currency-dropdown"
              direction="up"
              @changed="handleCurrencyChange"
            />
          </li>
          <li>
            <Button
              form="underline-invert"
              class="link"
              tag="a"
              href="/pages/terms-conditions"
              theme="secondary"
            >
              Terms &amp; Conditions
            </Button>
          </li>
          <li>
            <Button
              form="underline-invert"
              class="link"
              tag="a"
              href="/pages/privacy-policy"
              theme="secondary"
            >
              Privacy
            </Button>
          </li>
          <li>
            <Button
              form="underline-invert"
              class="link"
              tag="a"
              href="/pages/members-terms-conditions"
              theme="secondary"
            >
              Member Terms
            </Button>
          </li>
          <li>
            <Button
              form="underline-invert"
              class="link"
              tag="a"
              :href="helpUrl"
              theme="secondary"
            >
              Help
            </Button>
          </li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script>
import FooterLinks from "@/components/FooterLinks.vue";
import SlideLogin from "@/components/SlideLogin.vue";
import StoreRedirect from "@/components/StoreRedirect.vue";
import DropDown from "@/components/DropDown.vue";
import Button from "@/components/ButtonV2.vue";
import AW23TextCard from "@/components/homepage/AW23TextCard.vue";
import { mapActions, mapGetters } from "vuex";
import { getSignUpBonus } from "@/config";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import klarnaIcon from "@/assets/images/payment-methods/footer/klarna.png";
import paypalIcon from "@/assets/images/payment-methods/footer/paypal.png";
import mastercardIcon from "@/assets/images/payment-methods/footer/mastercard.png";
import visaIcon from "@/assets/images/payment-methods/footer/visa.png";
import amexIcon from "@/assets/images/payment-methods/footer/amex.png";
import applePayIcon from "@/assets/images/payment-methods/footer/apple-pay.png";
import stripeIcon from "@/assets/images/payment-methods/footer/stripe.png";
import afterpayIcon from "@/assets/images/payment-methods/footer/afterpay.png";

const paymentIcons = {
  klarna: { src: klarnaIcon, alt: "Klarna" },
  paypal: { src: paypalIcon, alt: "PayPal" },
  mastercard: { src: mastercardIcon, alt: "Mastercard" },
  visa: { src: visaIcon, alt: "Visa" },
  amex: { src: amexIcon, alt: "American Express" },
  applePay: { src: applePayIcon, alt: "Apple Pay" },
  stripe: { src: stripeIcon, alt: "Stripe" },
  afterpay: { src: afterpayIcon, alt: "Afterpay" },
};

const footerPaymentMethodsByCurrency = {
  gbp: {
    desktop: ["klarna", "paypal", "mastercard", "visa", "amex", "applePay"],
    mobile: ["klarna", "paypal", "mastercard", "visa", "applePay", "amex"],
  },
  usd: {
    desktop: [
      "stripe",
      "paypal",
      "mastercard",
      "visa",
      "amex",
      "klarna",
      "applePay",
    ],
    mobile: ["stripe", "paypal", "mastercard", "amex", "klarna", "applePay"],
  },
  eur: {
    desktop: ["klarna", "paypal", "mastercard", "visa", "amex", "applePay"],
    mobile: ["klarna", "paypal", "mastercard", "visa", "applePay", "amex"],
  },
  aud: {
    desktop: [
      "afterpay",
      "paypal",
      "mastercard",
      "visa",
      "amex",
      "klarna",
      "applePay",
    ],
    mobile: ["klarna", "paypal", "mastercard", "visa", "applePay", "amex"],
  },
};

export default {
  name: "FooterComponent",
  components: {
    Currency: DropDown,
    FooterLinks,
    SlideLogin,
    StoreRedirect,
    Button,
    AW23TextCard,
  },
  inject: ["isPlaywright"],
  data() {
    return {
      isVisible: true,
      dropdownoptions: [
        {
          value: "gbp",
          label: "GBP (£)",
        },
        {
          value: "eur",
          label: "EUR (€)",
        },
        {
          value: "usd",
          label: "USD ($)",
        },
        {
          value: "aud",
          label: "AUD ($)",
        },
      ],
    };
  },
  computed: {
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapGetters("app", ["currencyCode"]),
    signupBannerVisible() {
      return !this.isLoggedIn;
    },
    bonus() {
      if (!this.currencyCode) {
        return null;
      }
      const featureToggles = getFeatureToggles();
      const amount = getSignUpBonus(this.currencyCode, featureToggles);
      if (typeof amount === "string" && amount.endsWith("%")) {
        return amount;
      }
      return this.$filters.toCurrency(amount, this.currencyCode, 0);
    },
    text() {
      if (this.version === 5) {
        return "Create a Members Only account now";
      }
      return `Sign up & create a Members Only account to receive a ${this.bonus} discount for your first order`;
    },
    version() {
      const { dataCollectionVersion = 6 } = getFeatureToggles();
      return dataCollectionVersion;
    },
    helpUrl() {
      const helpUrls = {
        gbp: "https://help-gb.ratandboa.com/en-US",
        usd: "https://help-us.ratandboa.com/",
        eur: "https://help-eu.ratandboa.com/",
        aud: "https://help-au.ratandboa.com/",
      };
      return helpUrls[this.currencyCode] || helpUrls.gbp;
    },
    footerPaymentMethods() {
      const currency = (this.currencyCode || "").toLowerCase();
      const methods = footerPaymentMethodsByCurrency[currency];
      if (!methods) return [];

      const order = this.mq?.mdPlus ? methods.desktop : methods.mobile;
      return order.map((id) => paymentIcons[id]);
    },
  },
  methods: {
    ...mapActions("app", ["setSlideLogin", "setSlideSignUp", "changeCurrency"]),
    handleCurrencyChange(value) {
      this.changeCurrency(value);
    },
  },
};
</script>

<style lang="scss" scoped>
.footer {
  &__logo {
    width: 56px;
  }
}

.footer-grid {
  display: flex;
  justify-content: center;
  align-items: stretch;
  flex-direction: column;

  &__brand {
    display: contents;
  }

  @include respond-above(md) {
    padding: 0 24px;
    align-items: center;
    flex-direction: row;
    justify-content: space-between;
    gap: 20px;

    &__copyright,
    &__links {
      flex: 1;
    }
  }

  &--payments {
    .footer-grid__brand {
      display: contents;
    }

    .footer-grid__links {
      order: 1;
      padding-bottom: 12px;
    }

    .footer-grid__copyright {
      order: 2;
      width: 100%;
      flex: none;
      background-color: transparent;
      text-align: left;
      padding: 0 20px 10px;
    }

    .footer-grid__logo {
      order: 3;
      width: 100%;
      flex: none;
      background-color: $color-grey-light;
      text-align: center;
      padding: 20px 20px 0;

      .footer__logo {
        width: 32px;
      }
    }

    .footer-payments {
      order: 4;
      background-color: $color-grey-light;
      padding: 0 20px 30px;
    }

    @include respond-above(md) {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;

      .footer-grid__brand {
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: center;
        gap: 12px;
        order: 0;
        background-color: transparent;
      }

      .footer-grid__logo,
      .footer-grid__copyright {
        order: 0;
        width: auto;
        flex: none;
        text-align: left;
        padding: 0;
      }

      .footer-grid__logo {
        background-color: $color-white;
      }

      .footer-grid__logo .footer__logo {
        width: 56px;
      }

      .footer-payments {
        order: 0;
        margin-left: 93px;
        padding: 0;
        justify-content: flex-start;
        background-color: transparent;
      }

      .footer-grid__links {
        order: 0;
        margin-left: auto;
        flex: none;
        padding: 20px 0;
      }
    }
  }

  &__copyright {
    background-color: $color-grey-light;
    order: 3;
    text-align: center;
    padding-bottom: 24px;

    @include respond-above(md) {
      padding-bottom: 0;
      order: 1;
      text-align: left;
      background-color: transparent;
    }
  }

  &__logo {
    padding-top: 5px;
    order: 2;
    text-align: center;
    background-color: $color-grey-light;
    flex: 1 0 auto;

    @include respond-above(md) {
      padding-top: 0px;
      order: 2;
      flex: 0 0 auto;
      background-color: transparent;
    }
  }

  &__links {
    padding: 20px;
    order: 1;

    @include respond-above(md) {
      padding: 20px 0;
      order: 3;
      display: block;
    }
  }
}

.footer-payments {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    margin: 0;
    padding: 0;
  }

  &__icon {
    display: block;
    height: 32px;
    width: auto;
  }

  @include respond-above(md) {
    flex-wrap: nowrap;

    &__icon {
      height: 27px;
    }
  }
}

.copyright {
  @include utility-label(11px);
  color: #888;
  line-height: normal;
  text-transform: uppercase;
}

.currency-dropdown {
  :deep() {
    .button {
      padding: 0;
      background: transparent;
      border: none;
      color: $color-grey-dark;

      @include respond-above(md) {
        padding-right: 14px;
      }
    }

    .button .text {
      @include utility-label(12px);
    }
  }
}

.links {
  display: flex;
  gap: 20px;
  justify-content: flex-end;
  align-items: stretch;
  flex-direction: column;

  @include respond-above(md) {
    gap: 24px;
    flex-direction: row;
    align-items: center;
  }
}

.link {
  @include utility-label(12px);
}

.loyalty {
  background: $color-grey-light;
  display: flex;
  justify-content: center;
}
</style>
